/* eslint-disable jsx-a11y/anchor-is-valid */
import React from 'react'
import * as Yup from 'yup'
import clsx from 'clsx'
import {useFormik} from 'formik'
import {useIntl} from 'react-intl'
import {Link} from 'react-router-dom'
import {authAPI} from '../../../services/AuthService'
import {useAppDispatch} from '../../../hooks/redux'
import {loginAction} from '../../../slices/authSlice'
import {usersAPI} from '../../../services/UsersService'

const loginSchema = (f: any) =>
  Yup.object().shape({
    email: Yup.string()
      .email(f({id: 'FORMIK.ERROR.WRONGEMAILFORMAT'}))
      .min(3, f({id: 'FORMIK.ERROR.MIN'}))
      .max(50, f({id: 'FORMIK.ERROR.MAX'}))
      .required(f({id: 'EMAIL.LABEL'}) + f({id: 'FORMIK.ERROR.REQUIREDFIELD'})),
    password: Yup.string()
      .min(3, f({id: 'FORMIK.ERROR.MIN'}))
      .max(50, f({id: 'FORMIK.ERROR.MAX'}))
      .required(f({id: 'PASSWORD.LABEL'}) + f({id: 'FORMIK.ERROR.REQUIREDFIELD'})),
  })

const initialValues = {
  email: '',
  password: '',
}

export function Login() {
  const intl = useIntl()
  const dispatch = useAppDispatch()
  const [userLogin, {isLoading}] = authAPI.useLoginMutation()
  const [getCurrentUser] = usersAPI.useLazyFetchGetMeQuery()

  const formik = useFormik({
    initialValues,
    validationSchema: loginSchema(intl.formatMessage),
    onSubmit: async (values, {setStatus, setSubmitting}) => {
      await userLogin({username: values.email, password: values.password})
        .unwrap()
        .then(async (result) => {
          dispatch(loginAction(result))
          await getCurrentUser()
        })
        .catch((error) => {
          setStatus(intl.formatMessage({id: 'AUTH.SIGNIN.FAILED'}))
          setSubmitting(false)
        })
    },
  })

  return (
    <>
      <form className='form w-100' onSubmit={formik.handleSubmit} noValidate id='kt_login_signin_form'>
        <div className='text-center mb-10'>
          <h1 className='text-dark mb-3 fs-5'>{intl.formatMessage({id: 'AUTH.SIGNIN.TITLE'})}</h1>
          <Link to='/auth/registration' className='link-primary fs-5 fw-bolder'>
            {intl.formatMessage({id: 'AUTH.SIGNUP.TITLE'})}
          </Link>
        </div>

        {formik.status ? (
          <div className='mb-lg-15 alert alert-danger'>
            <div className='alert-text font-weight-bold'>{formik.status}</div>
          </div>
        ) : null}

        <div className='fv-row mb-10'>
          <label className='form-label fs-6 fw-bolder text-dark'>{intl.formatMessage({id: 'EMAIL.LABEL'})}</label>
          {formik.touched.email && formik.errors.email && (
            <div className='fv-plugins-message-container text-center mb-2'>
              <div className='fv-help-block'>
                <span role='alert'>{formik.errors.email}</span>
              </div>
            </div>
          )}
          <input
            placeholder={intl.formatMessage({id: 'EMAIL.PLACEHOLDER'})}
            {...formik.getFieldProps('email')}
            className={clsx(
              'form-control form-control-lg form-control-solid',
              {'is-invalid': formik.touched.email && formik.errors.email},
              {
                'is-valid': formik.touched.email && !formik.errors.email,
              }
            )}
            type='email'
            name='email'
            autoComplete='off'
          />
        </div>

        <div className='fv-row mb-10'>
          <div className='d-flex justify-content-between mt-n5'>
            <div className='d-flex flex-stack mb-2'>
              <label className='form-label fw-bolder text-dark fs-6 mb-0'>
                {intl.formatMessage({id: 'PASSWORD.LABEL'})}
              </label>
              <Link to='/auth/forgot-password' className='link-primary fs-6 fw-bolder' style={{marginLeft: '5px'}}>
                {intl.formatMessage({id: 'PASSWORD.FORGOT'})}?
              </Link>
            </div>
          </div>
          {formik.touched.password && formik.errors.password && (
            <div className='fv-plugins-message-container text-center mb-2'>
              <div className='fv-help-block'>
                <span role='alert'>{formik.errors.password}</span>
              </div>
            </div>
          )}
          <input
            placeholder={intl.formatMessage({id: 'PASSWORD.PLACEHOLDER'})}
            type='password'
            autoComplete='off'
            {...formik.getFieldProps('password')}
            className={clsx(
              'form-control form-control-lg form-control-solid',
              {
                'is-invalid': formik.touched.password && formik.errors.password,
              },
              {
                'is-valid': formik.touched.password && !formik.errors.password,
              }
            )}
          />
        </div>

        <div className='text-center'>
          <button
            type='submit'
            id='kt_sign_in_submit'
            className='btn btn-lg btn-primary w-100 mb-5'
            disabled={formik.isSubmitting || !formik.isValid}
          >
            {!isLoading && <span className='indicator-label'>{intl.formatMessage({id: 'BUTTON.SUBMIT'})}</span>}
            {isLoading && (
              <span className='indicator-progress' style={{display: 'block'}}>
                {intl.formatMessage({id: 'WAIT'})}
                <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
              </span>
            )}
          </button>
        </div>
      </form>
    </>
  )
}
